/* eslint-disable */
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { apolloError, exLog, getApiUrl } from "./components/commons";
import { getProjectStore } from "./store/projectStore";

// If running on a PR preview environment (e.g., https://pr-514.test.appfactor.cloud/),
// ensure localStorage.appf_tenant_info is set to Cognito dev defaults so the app can boot.
try {
  const host = typeof window !== 'undefined' ? (window.location && window.location.hostname) : '';
  const isPRPreview = !!host && /^pr-\d+\./i.test(host) && host.indexOf('.test.appfactor.cloud') !== -1;
  if (isPRPreview) {
    const prTenantInfo = {
      clientId: "55gf19fprt1vejhb1gt38mhl60",
      config: { clientId: "55gf19fprt1vejhb1gt38mhl60", poolId: "eu-west-2_TpjF7jx55" },
      poolId: "eu-west-2_TpjF7jx55",
      sku: "full-internal",
      api: "https://api.dev.appfactor.cloud",
      type: "cognito"
    };

    // Only set if not present or if different
    let shouldWrite = true;
    try {
      const existing = localStorage.appf_tenant_info ? JSON.parse(localStorage.appf_tenant_info) : null;
      if (existing && existing.type === 'cognito' && existing.clientId === prTenantInfo.clientId && existing.poolId === prTenantInfo.poolId && existing.api === prTenantInfo.api) {
        shouldWrite = false;
      }
    } catch (_) { /* ignore and overwrite */ }

    if (shouldWrite) {
      localStorage.appf_tenant_info = JSON.stringify(prTenantInfo);
    }
  }
} catch (_) { /* noop */ }

let currentPool = localStorage.getItem("tenantData");

const _RETRY_MAX_ATTEMPTS = localStorage.getItem("maxRetry") || 5;
export let appf_tenant_info = {
  _:1
}

if (localStorage.appf_tenant_info) {
  try {
    appf_tenant_info = JSON.parse(localStorage.appf_tenant_info);
  } catch (e) {
    console.error("tenant_info.json invalid. Please contact support.");

    localStorage.removeItem("appf_tenant_info");
  }
}

fetch(`${window.location.origin}/tenant_info.json`).then(response => response.text()).then(data => { 
  try {
    appf_tenant_info = JSON.parse(data);
    localStorage.appf_tenant_info = data;
  } catch (e) {  
    console.error("tenant_info.json invalid. Please contact support.");
  }
})

if(currentPool !== null ){
  currentPool = JSON.parse(currentPool);

  if (currentPool.poolId == "" || !currentPool.poolId) {
    currentPool.poolId = "_";
    currentPool.clientId = "_";
  }
}

export let poolData = {
  accessToken: localStorage.token || "",
  refreshToken: () => {},
  config: (currentPool != null) ? currentPool.config : null,
  UserPoolId: (currentPool != null) ? currentPool?.poolId || "" : "",
  ClientId: (currentPool != null) ? currentPool?.clientId || "" : "",
  tenant: currentPool ? currentPool.tenant : "",
  envURL: currentPool ? currentPool.envURL : ""
};

// Deduplicates concurrent calls to ensureProjectEnvReady — only one fetch in flight at a time
let _ensureProjectEnvReadyPromise = null;

// Exported helper to ensure project/env are ready before running queries
export function ensureProjectEnvReady(accTIn) {
  if (getProjectStore().projectsReady) {
    return Promise.resolve(true);
  }
  if (_ensureProjectEnvReadyPromise) {
    return _ensureProjectEnvReadyPromise;
  }
  _ensureProjectEnvReadyPromise = _doEnsureProjectEnvReady(accTIn).finally(() => {
    _ensureProjectEnvReadyPromise = null;
  });
  return _ensureProjectEnvReadyPromise;
}

async function _doEnsureProjectEnvReady(accTIn) {
  try {
    if (getProjectStore().projectsReady) {
      return true;
    }

    let accT = accTIn;
    if (!accT || accT.length === 0) {
      try { accT = poolData.accessToken || appf_tenant_info?.session?.accessToken || ""; } catch (_) { /* */ }
    }
    if (!accT || accT.length === 0) {
      // Cannot proceed without token
      return false;
    }

    const prjName = localStorage.prjName ? localStorage.prjName : "projects";
    const baseUrl = getApiUrl(poolData.envURL);

    // Fetch projects list
    const url = `${baseUrl}/mgmt/${prjName}`;
    const response = await fetch(url, { headers: { Authorization: `Bearer ${accT}` } });
    if (!response.ok) { throw new Error(`Response status: ${response.status}`); }
    const json = await response.json();

    // Persist and expose
    let projects;
    try {
      projects = JSON.parse(JSON.stringify(json));
      localStorage.projects = JSON.stringify(json);
    } catch (_) { /* */ }

    // Determine project ID: prefer stored value, fall back to default or first list item
    let projectId = localStorage.lastProjectId;
    if (!projectId || projectId === "undefined") {
      projectId = (window._g && window._g._DEFAULT_PROJECT_ID) || (Array.isArray(json) && json[0]?.id) || "";
    }
    if (projectId) {
      localStorage.lastProjectId = projectId;
    }

    // Determine source env ID: prefer stored value, else fetch
    let sEnvId = localStorage.lastSEnvId;
    let sEnv;
    if (!sEnvId || sEnvId === "undefined") {
      const urlEnv = `${baseUrl}/mgmt/environments/source/${prjName}/${projectId}`;
      const responseEnv = await fetch(urlEnv, { headers: { Authorization: `Bearer ${accT}` } });
      const jsonEnv = await responseEnv.json();
      try { sEnv = JSON.parse(JSON.stringify(jsonEnv)); } catch (_) { /* */ }
      sEnvId = (Array.isArray(jsonEnv) && jsonEnv[0]?.id) || (window._g && window._g._DEFAULT_SOURCE_ENV_ID) || "";
    }
    if (sEnvId) {
      localStorage.lastSEnvId = sEnvId;
    }

    getProjectStore().setProjectContext({
      currentProjectId: projectId,
      currentSEnv:      sEnvId,
      projects,
      sEnv,
    });
    return true;
  } catch (e) {
    console.warn('[ensureProjectEnvReady] Failed to fetch projects, using cached/default values:', e.message);
    // Fall back to cached projects from localStorage or defaults
    try {
      // Try to use cached projects list from localStorage
      let projects;
      if (localStorage.projects) {
        try {
          projects = JSON.parse(localStorage.projects);
        } catch (_) { /* Invalid JSON in cache */ }
      }

      // Use cached or default project ID
      const projectId = localStorage.lastProjectId ||
                        (window._g && window._g._DEFAULT_PROJECT_ID) ||
                        getProjectStore().currentProjectId;
      if (projectId) {
        localStorage.lastProjectId = projectId;
      }

      // Use cached or default source env ID
      const sEnvId = localStorage.lastSEnvId ||
                     (window._g && window._g._DEFAULT_SOURCE_ENV_ID) ||
                     getProjectStore().currentSEnv;
      if (sEnvId) {
        localStorage.lastSEnvId = sEnvId;
      }

      // Mark as successful if we have at least project ID from cache
      if (projectId && sEnvId) {
        getProjectStore().setProjectContext({
          currentProjectId: projectId,
          currentSEnv:      sEnvId,
          projects,
          sEnv:             null,
        });
        return true;
      }
    } catch (_) { /* */ }
    return false;
  }
}

export function getNewApolloClient(inUrl, inPoolData, addTypename = true) {

  let _getProjectsPromise = null;
  async function getProjects(accT) {

    if (getProjectStore().projectsReady) {
      return;
    }
    if (_getProjectsPromise) {
      return _getProjectsPromise;
    }
    _getProjectsPromise = _fetchProjects(accT).finally(() => { _getProjectsPromise = null; });
    return _getProjectsPromise;
  }

  async function _fetchProjects(accT) {
    
    const prjName = localStorage.prjName ? localStorage.prjName : "projects";
    
    const url = getApiUrl(poolData.envURL) + `/mgmt/${prjName}`;
    const accessToken = accT || inPoolData.accessToken;

    try {
      const response = await fetch(url,{
        headers: {
          "Authorization": `Bearer ${accessToken}`
        } 
      });
      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }
  
      const json = await response.json();

      try{
        const projects = JSON.parse(JSON.stringify(json));
        localStorage.projects = JSON.stringify(json);

        let projectId = localStorage.lastProjectId;
        if (typeof projectId === "undefined") {
          projectId = window._g._DEFAULT_PROJECT_ID;
          localStorage.lastProjectId = projectId;
        }

        const urlEnv = getApiUrl(poolData.envURL) + `/mgmt/environments/source/${prjName}/${projectId}`;
        const responseEnv = await fetch(urlEnv, {
          headers: { "Authorization": `Bearer ${accessToken}` }
        });
        const jsonEnv = await responseEnv.json();
        const sEnv = JSON.parse(JSON.stringify(jsonEnv));
        const sEnvId = jsonEnv[0].id;
        localStorage.lastSEnvId = sEnvId;

        getProjectStore().setProjectContext({
          currentProjectId: projectId,
          currentSEnv:      sEnvId,
          projects,
          sEnv,
        });
      }catch(e){
        /**/
       }


      return json;
    } catch (e) {
      console.warn('[getProjects] Failed to fetch projects, using cached/default values:', e.message);

      // Try to use cached projects list from localStorage
      let cachedProjects = {};
      if (localStorage.projects) {
        try {
          cachedProjects = JSON.parse(localStorage.projects);
        } catch (_) { /* Invalid JSON in cache */ }
      }

      // Use cached or default project ID
      const projectId = localStorage.lastProjectId ||
                        (window._g && window._g._DEFAULT_PROJECT_ID) ||
                        getProjectStore().currentProjectId;
      if (projectId) {
        localStorage.lastProjectId = projectId;
      }

      // Use cached or default source env ID
      const sEnvId = localStorage.lastSEnvId ||
                     (window._g && window._g._DEFAULT_SOURCE_ENV_ID) ||
                     getProjectStore().currentSEnv;
      if (sEnvId) {
        localStorage.lastSEnvId = sEnvId;
      }

      // Mark as successful if we have at least project ID from cache
      if (projectId && sEnvId) {
        getProjectStore().setProjectContext({
          currentProjectId: projectId,
          currentSEnv:      sEnvId,
          projects:      cachedProjects,
          sEnv:             null,
        });
      }

      return cachedProjects;
    }
  }

  // Wait briefly for a token to be available after login
  async function waitForToken(timeoutMs = 2000) {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      try {
        const t = (inPoolData && inPoolData.accessToken) || poolData.accessToken || (appf_tenant_info?.session?.accessToken) || localStorage.token || '';
        if (t && typeof t === 'string' && t.length > 0) return t;
      } catch (_) { /* */ }
      try { inPoolData && inPoolData.refreshToken && inPoolData.refreshToken(); } catch (_) { /* */ }
      await new Promise(r => setTimeout(r, 100));
    }
    return '';
  }

  // Configure cache with typePolicies instead of addTypename
  // addTypename defaults to true in Apollo Client 3, so we only need special config if it was explicitly false
  const cacheConfig = {
    typePolicies: {
      Query: {
        fields: {
          getCurrentApplications: {
            // Custom merge function to handle ApplicationInstancePage without losing cache data
            merge(existing, incoming, { mergeObjects }) {
              // If no existing data, just return incoming
              if (!existing) {
                return incoming;
              }

              // Merge the objects, preferring incoming data but preserving fields that aren't in incoming
              return mergeObjects(existing, incoming);
            }
          }
        }
      }
    }
  };

  // If addTypename was explicitly set to false, configure the cache to skip __typename
  // Note: This is rarely needed and addTypename=true is the standard behavior
  if (addTypename === false) {
    // In Apollo Client 3+, to achieve the old addTypename: false behavior,
    // you would need to strip __typename from results manually or modify queries.
    // However, this is not recommended. We'll just use default behavior.
    console.warn('[Apollo Client]: addTypename parameter is deprecated. Using default behavior (includes __typename).');
  }

  return new ApolloClient({
    link:  apolloError.concat(
      new RetryLink({
        attempts: (count, operation, error) => {

          // CoreStoreProvider manages its own retries — skip Apollo RetryLink
          if (operation.getContext().noRetry) return false;

          try{
            try{
              inPoolData.refreshToken();
              accT = inPoolData.accessToken;
            }catch(e){/**/}
            // Do not call async azureRefreshToken here; it returns a Promise and breaks headers
            // If needed, token should already be populated by the auth flow
          }catch(e){/**/}


          if(count>_RETRY_MAX_ATTEMPTS){
            return false;
          }else{
            console.log("RETRY --------------------",count, operation, error,arguments)
            return !!error && operation.operationName != 'specialCase';  
          }
        },
        delay: {
          initial: 420,
          max: 4000,
          jitter: true
        },
      })).concat(

      // Ensure project/env are ready BEFORE composing headers for the first request post-login
      setContext(async () => {
        try {
          const tok = await waitForToken(2000);
          if (tok) {
            await ensureProjectEnvReady(tok);
          }
        } catch (e) { /* fail-open */ }
        return {};
      })
    ).concat(

      new ApolloLink(
      (operation, forward) => {

        let accT = inPoolData.accessToken;

        try{
          inPoolData.refreshToken();
          accT = inPoolData.accessToken;
        }catch(e){/**/}
        // Avoid assigning Promise returned by azureRefreshToken to header; rely on current token only
        // try{
        //   if(appf_tenant_info.azureRefreshToken){
        //     accT = appf_tenant_info.azureRefreshToken()
        //   }
        // }catch(e){/**/}

        if(window.extendedLog){ exLog("T1", arguments , accT );}

        if(typeof accT !== 'undefined' && accT.length == 0 ){
          try{
            inPoolData.refreshToken();
            accT = inPoolData.accessToken;
          }catch(e){/**/}

          try{
            if(appf_tenant_info.azureRefreshToken){
              appf_tenant_info.azureRefreshToken()
            }
          }catch(e){/**/}
        }

        if(window.extendedLog){ exLog("T2", arguments , accT );}

        if( accT == ""){
          try{
            accT = appf_tenant_info.session.accessToken
          }catch(e){
            /**/
          }

          if(window.extendedLog){ exLog("T3", arguments , accT );}
        }
        
        if (!getProjectStore().projectsReady && accT) {
          getProjects(accT);
        }

        const headers = { };
        if (accT && typeof accT === 'string' && accT.length > 0) {
          headers["authorization"] = `Bearer ${accT}`;
        }
        // Always provide project/env headers using robust fallbacks
        try {
          const prjId = (
            getProjectStore().currentProjectId ||
            (typeof localStorage !== 'undefined' && localStorage.lastProjectId) ||
            (typeof window !== 'undefined' && window._g && window._g._DEFAULT_PROJECT_ID) ||
            ''
          );
          const prjHeader = `${prjId || ''}`;
          headers["X-Project-Id"] = prjHeader;
          headers["x-project-id"] = prjHeader;
        } catch (_) { /* noop */ }
        try {
          const envId = (
            getProjectStore().currentSEnv ||
            (typeof localStorage !== 'undefined' && localStorage.lastSEnvId) ||
            (typeof window !== 'undefined' && window._g && window._g._DEFAULT_SOURCE_ENV_ID) ||
            ''
          );
          const envHeader = `${envId || ''}`;
          headers["X-Environment-Id"] = envHeader;
          headers["x-environment-id"] = envHeader;
        } catch (_) { /* noop */ }

        operation.setContext({
            headers
          });

        return forward(operation);
      },
    ))
    .concat(
        new HttpLink({ 
          uri: inUrl,
          enhancedClientAwareness: { transport: false }
        })
      ),
    cache: new InMemoryCache(cacheConfig),
    onError:({ graphQLErrors, networkError, operation, forward }) => {
      console.log("ApolloClient - ERROR ---- ",graphQLErrors, networkError, operation, forward)
      if (graphQLErrors) {
        for (let err of graphQLErrors) {
          switch (err.extensions.code) {
            // Apollo Server sets code to UNAUTHENTICATED
            // when an AuthenticationError is thrown in a resolver
            case "UNAUTHENTICATED":
            {
              let accT = inPoolData.refreshToken();
              accT = inPoolData.accessToken;

              let oldHeaders = operation.getContext().headers;
              operation.setContext({
                headers: {
                  ...oldHeaders,
                  authorization: accT? `Bearer ${accT}` : ''
                },
              });
              // Retry the request, returning the new observable
              return forward(operation);
            }
          }
        }
      }
    
      // To retry on network errors, we recommend the RetryLink
      // instead of the onError link. This just logs the error.
      if (networkError) {
        console.log(`[Network error]: ${networkError}`);
      }
    }
  })
}
